import React, { useEffect, useRef } from 'react';
import Hls from 'hls.js';
import { initHlsPlayer } from '../hlsHelper.js';
import { renderVideoToCanvas } from '../mediaHelper.js';
import { smoothedRotationAt } from '../rotationHelper.js';
import { accessHeaders as fetchAccessHeaders } from '../sessionAccess.js';

/**
 * Lecteur vidéo secondaire pour la deuxième caméra (flux front).
 * Il synchronise la vidéo avec le lecteur principal sans diffuser d'audio
 * pour éviter les échos.
 */
export default function DualCameraStreamPane({
    streamUrl,
    sessionKey,
    backendUrl,
    accessHeaders = null,
    mainVideoElement,
    rotationMode = 'locked',
    label = 'Caméra Avant'
}) {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const canvasWrapperRef = useRef(null);
    const hlsRef = useRef(null);
    const id3CuesRef = useRef([]);
    const animFrameRef = useRef(null);
    const headersRef = useRef(accessHeaders || {});

    useEffect(() => {
        if (!accessHeaders) {
            fetchAccessHeaders().then(h => { headersRef.current = h; }).catch(() => {});
        }
    }, [accessHeaders]);

    useEffect(() => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const wrapper = canvasWrapperRef.current;
        if (!video || !canvas || !streamUrl) return;

        const updateCanvasSize = () => {
            const w = wrapper?.clientWidth || 640;
            const h = wrapper?.clientHeight || 480;
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            if (w > 0 && h > 0) {
                canvas.width = Math.round(w * dpr);
                canvas.height = Math.round(h * dpr);
                canvas.style.width = `${w}px`;
                canvas.style.height = `${h}px`;
            }
        };
        updateCanvasSize();
        const resizeObserver = new ResizeObserver(updateCanvasSize);
        if (wrapper) resizeObserver.observe(wrapper);

        video.muted = true;
        video.playsInline = true;

        const effectiveHeaders = accessHeaders || headersRef.current;
        const hls = initHlsPlayer(
            video,
            streamUrl,
            sessionKey,
            backendUrl,
            undefined,
            effectiveHeaders
        );
        hlsRef.current = hls;

        if (hls) {
            hls.on(Hls.Events.FRAG_PARSING_METADATA, (event, data) => {
                if (data?.samples?.length) {
                    const fragStart = data.frag?.start ?? 0;
                    for (const sample of data.samples) {
                        if (sample.data) {
                            try {
                                const rawStr = new TextDecoder('utf-8').decode(sample.data);
                                const matchRot = rawStr.match(/ROTATE:(\d+(?:\.\d+)?)/i);
                                const matchTs = rawStr.match(/TS:(\d+)/i);
                                const rot = matchRot ? parseFloat(matchRot[1]) : null;
                                const ts = matchTs ? parseInt(matchTs[1], 10) : null;
                                if (rot !== null) {
                                    id3CuesRef.current.push({
                                        pts: sample.pts ?? fragStart,
                                        rot: ((rot % 360) + 360) % 360,
                                        ts
                                    });
                                    id3CuesRef.current.sort((a, b) => a.pts - b.pts);
                                }
                            } catch (_) {}
                        }
                    }
                }
            });
        }

        const renderFrame = () => {
            if (canvas && video && video.readyState >= 2) {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    let rot = 0;
                    if (id3CuesRef.current.length > 0) {
                        const smoothed = smoothedRotationAt(id3CuesRef.current, video.currentTime);
                        rot = smoothed === null ? id3CuesRef.current[0].rot : smoothed;
                    }
                    if (rotationMode === 'locked') {
                        rot = Math.round(rot / 90) * 90;
                    }
                    renderVideoToCanvas(ctx, video, canvas, rot, false, true);
                }
            }
            animFrameRef.current = requestAnimationFrame(renderFrame);
        };
        renderFrame();

        return () => {
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [streamUrl, sessionKey, backendUrl, accessHeaders, rotationMode]);

    // Synchronisation de la lecture avec la vidéo principale
    useEffect(() => {
        if (!mainVideoElement) return;

        const syncWithMain = () => {
            const video = videoRef.current;
            if (!video || !mainVideoElement) return;

            if (mainVideoElement.paused && !video.paused) {
                video.pause();
            } else if (!mainVideoElement.paused && video.paused) {
                video.play().catch(() => {});
            }

            // Sync seek if drifted more than 0.5s
            if (Number.isFinite(mainVideoElement.currentTime) && Number.isFinite(video.currentTime)) {
                if (Math.abs(mainVideoElement.currentTime - video.currentTime) > 0.5) {
                    try {
                        video.currentTime = mainVideoElement.currentTime;
                    } catch (_) {}
                }
            }
        };

        const interval = setInterval(syncWithMain, 500);
        mainVideoElement.addEventListener('play', syncWithMain);
        mainVideoElement.addEventListener('pause', syncWithMain);
        mainVideoElement.addEventListener('seeked', syncWithMain);

        return () => {
            clearInterval(interval);
            mainVideoElement.removeEventListener('play', syncWithMain);
            mainVideoElement.removeEventListener('pause', syncWithMain);
            mainVideoElement.removeEventListener('seeked', syncWithMain);
        };
    }, [mainVideoElement]);

    return (
        <div className="sid-dual-stream-pane">
            <div className="sid-dual-stream-label">{label}</div>
            <div className="canvas-wrapper" ref={canvasWrapperRef}>
                <canvas ref={canvasRef} width="640" height="640" />
            </div>
            <video
                ref={videoRef}
                crossOrigin="anonymous"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '1px',
                    height: '1px',
                    opacity: 0.001,
                    pointerEvents: 'none',
                    zIndex: -10
                }}
                playsInline
                muted
            />
        </div>
    );
}
